import React, { createContext, useState, useContext, useEffect } from 'react';
import { fetchAllRecordsWithProgress, TOTAL_RECORD_COUNT } from '../utils/airtable';

// Create a context for sharing data across components
export const DataContext = createContext();

// Custom hook to use the data context
export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Load data only once when the app initializes
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Set the hardcoded total count immediately
        setTotalCount(TOTAL_RECORD_COUNT);
        
        // Fetch data with progress reporting
        const records = await fetchAllRecordsWithProgress((count) => {
          if (isMounted) {
            setLoadedCount(count);
          }
        });
        
        // Process records to match our data structure
        const processedRecords = records.map(record => {
          // Normalize field names for case-insensitive matching
          const normalizedFields = {};
          Object.keys(record).forEach(key => {
            normalizedFields[key.toLowerCase()] = key;
          });
          
          // Helper function to get a field value with case-insensitive matching
          const getField = (fieldNames) => {
            for (const name of fieldNames) {
              const normalizedName = name.toLowerCase();
              if (normalizedFields[normalizedName]) {
                return record[normalizedFields[normalizedName]] || '';
              }
            }
            return '';
          };
          
          // Get topic field and ensure it's a properly formatted array
          const topicField = getField(['Topic', 'Type']);
          let categories = [];
          
          if (Array.isArray(topicField)) {
            // If it's already an array, use it directly
            categories = topicField;
          } else if (typeof topicField === 'string' && topicField.includes(',')) {
            // If it's a comma-separated string, split it into an array
            categories = topicField.split(',').map(c => c.trim()).filter(c => c.length > 0);
          } else if (topicField) {
            // If it's a single value, make it an array
            categories = [topicField];
          }
          
          const baseRecord = {
            id: record.id,
            recommendation: getField(['Name', 'Recommendation']),
            details: getField(['Details', 'Description']),
            justification: getField(['Justification']),
            fullText: getField(['Full text', 'FullText', 'Full Text']),
            topic: categories, // Store as array
            organization: getField(['Organization', 'Source']),
            document: getField(['Document']),
            url: getField(['URL', 'Url', 'Link']),
            orgType: getField(['Org Type']), // Add orgType field
            similarRecommendations: getField(['Similar recommendations', 'Similar Recommendations', 'SimilarRecommendations']) // Add similar recommendations field
          };

          // Safely get assignee value and convert to string if needed
          const rawAssignee = getField(['Assignee', 'Owner']);
          let assigneeString = '';
          
          if (Array.isArray(rawAssignee)) {
            // If it's an array, join with commas
            assigneeString = rawAssignee.join(',');
          } else if (typeof rawAssignee === 'object' && rawAssignee !== null) {
            // If it's an object (like from Airtable), try to get string value
            assigneeString = rawAssignee.toString();
          } else {
            // Otherwise convert to string
            assigneeString = String(rawAssignee);
          }

          // Return a single record with the full assignee string
          return {
            ...baseRecord,
            assignee: assigneeString
          };
        });
        
        if (isMounted) {
          setData(processedRecords);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        if (isMounted) {
          setError('Failed to load data. Please refresh the page.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Value to be provided to consuming components
  const value = {
    data,
    loading,
    error,
    loadedCount,
    totalCount
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}; 